import React from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

interface SectionTitleProps {
  children: React.ReactNode;
  className?: string;
  align?: "left" | "center" | "right";
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  /** Optional lead copy rendered under the title. */
  subtitle?: React.ReactNode;
  /** Show decorative quote-mark icons on both sides of the heading. */
  showQuoteMarks?: boolean;
  /** Custom quote-mark image URL. When omitted a built-in SVG ornament is used. */
  quoteMarkSrc?: string | null;
}

function DefaultQuoteMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 96"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("h-14 w-7 text-primary md:h-16 md:w-8", className)}
      aria-hidden="true"
    >
      <path
        d="M24 8c-6 10-14 22-14 36 0 12 8 22 18 22s16-8 16-18c0-8-5-14-12-14-3 0-6 1-8 3 1-10 6-20 12-29H24z"
        fill="currentColor"
        opacity="0.85"
      />
      <path
        d="M10 40c2 8 8 14 14 18-4 2-8 6-8 12 0 8 6 14 14 14 10 0 16-8 16-18 0-14-10-28-18-40H10z"
        fill="currentColor"
        opacity="0.45"
      />
    </svg>
  );
}

function QuoteMark({
  src,
  mirrored,
}: {
  src?: string | null;
  mirrored?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center self-center",
        mirrored && "-scale-x-100",
      )}
      aria-hidden="true"
    >
      {src ? (
        <span className="relative h-14 w-7 md:h-16 md:w-8">
          <Image
            src={src}
            alt=""
            fill
            unoptimized
            className="object-contain"
          />
        </span>
      ) : (
        <DefaultQuoteMark />
      )}
    </span>
  );
}

export function SectionTitle({
  children,
  className,
  align = "left",
  as = "h2",
  subtitle,
  showQuoteMarks = false,
  quoteMarkSrc = null,
}: SectionTitleProps) {
  const alignClasses = {
    left: "text-start",
    center: "text-center",
    right: "text-end",
  };

  const Tag = as;
  const hasSubtitle =
    subtitle != null &&
    !(typeof subtitle === "string" && subtitle.trim() === "");

  const textBlock = (
    <div className={cn("inline-block", alignClasses[align])}>
      <Tag className="mb-3 text-xl font-bold md:mb-4 md:text-2xl lg:text-3xl">
        {children}
      </Tag>
      {hasSubtitle && (
        <p className="mx-auto mt-1 max-w-2xl text-sm font-normal leading-relaxed text-gray-600 md:text-base">
          {subtitle}
        </p>
      )}
      <div className="mx-auto h-1 w-[80px] bg-primary md:w-[100px] lg:w-[115px]" />
    </div>
  );

  if (!showQuoteMarks) {
    return <div className={cn(alignClasses[align], className)}>{textBlock}</div>;
  }

  return (
    <div
      className={cn(
        "flex items-center justify-center gap-3 md:gap-5",
        alignClasses[align],
        className,
      )}
    >
      <QuoteMark src={quoteMarkSrc} />
      {textBlock}
      <QuoteMark src={quoteMarkSrc} mirrored />
    </div>
  );
}
